import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Dialog, useMediaQuery, Paper, PaperProps, DialogProps } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Draggable from 'react-draggable';

interface GhostDialogProps extends DialogProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  customMaxWidth?: string;
  className?: string;
  disablePortal?: boolean;
  type?: any;
}

const PaperComponent = React.forwardRef((props: PaperProps, ref: React.Ref<HTMLDivElement>) => {
  const nodeRef = useRef(null);
  const [bounds, setBounds] = useState({ left: 0, top: 0, right: 0, bottom: 0 });

  useEffect(() => {
    const updateBounds = () => {
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;
      const dialogHeader = document.getElementById('draggable-dialog-title');

      if (dialogHeader) {
        const rect = dialogHeader.getBoundingClientRect();
        setBounds({
          left: -rect.left,
          top: -rect.top,
          right: windowWidth - rect.right,
          bottom: windowHeight - rect.bottom
        });
      }
    };

    updateBounds();
    window.addEventListener('resize', updateBounds);
    return () => window.removeEventListener('resize', updateBounds);
  }, []);
  return (
    <Draggable handle="#draggable-dialog-title" cancel={'[class*="MuiDialogContent-root"]'} bounds={bounds} nodeRef={nodeRef}>
      <Paper ref={nodeRef} {...props} elevation={8} />
    </Draggable>
  );
});

const GhostDialog: React.FC<GhostDialogProps> = ({
  open,
  onClose,
  children,
  customMaxWidth,
  maxWidth = 'sm',
  className = '',
  disablePortal = false,
  type = '',
  ...props
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  /**
   * prevent dialog getting dismissed on tapping outside.
   */
  const _onClose = useCallback(
    (event: any, reason: string) => {
      if (reason !== 'backdropClick') {
        onClose();
      }
    },
    [onClose]
  );

  return (
    <Dialog
      fullScreen={type === 'moveToProd' ? false : fullScreen}
      onClose={_onClose}
      PaperComponent={PaperComponent}
      open={open}
      maxWidth={customMaxWidth ? false : maxWidth}
      sx={{
        ...props.sx,
        ...(customMaxWidth && {
          maxWidth: customMaxWidth,
          marginLeft: 'auto',
          marginRight: 'auto'
        }),
        maxHeight: '100%',
        height: 'auto'
      }}
      fullWidth
      className={`${className}`}
      disableEscapeKeyDown
      hideBackdrop
      disablePortal={disablePortal}
      PaperProps={{
        className: 'popup-content-background-image',
        sx: {
          backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : theme.palette.background.default
        }
      }}
    >
      {children}
    </Dialog>
  );
};

export default GhostDialog;
